import { FC } from 'react';
import UniversalLink from 'src/common/components/UniversalLink';
import Typography from 'src/common/components/Typography';
import Icon from 'src/common/components/IconDS22';
import type { Breadcrumb } from 'src/common/types/api/v4/boats/search';
import { useTranslation } from 'next-i18next';
import styles from './ListingCardMinimal.module.scss';

export type ListingCardMinimalProps = {
  breadcrumbs?: Breadcrumb[];
  reviewAverage?: string;
  primaryCategory?: number | null;
  headline?: string;
  /** The href this card should link */
  href: string;
  /**
   * Designed to accept urls constructed by search-go;
   * e.g. `https://img.getmyboat.com/images/54d55f146d5e1/processed.jpg?` (sometimes missing the '?')
   * any url params will be stripped and re-added by this component
   */
  imgSrc?: string;
  /**
   * The maximum width to optimize the image. This does not affect css layout.
   * If the image looks blurry, try increasing this value.
   * Lower values result in smaller file sizes.
   */
  imgOptimizationWidth: number;
};

const ListingCardMinimal: FC<ListingCardMinimalProps> = ({
  breadcrumbs,
  primaryCategory,
  reviewAverage,
  headline,
  imgSrc,
  imgOptimizationWidth,
  href,
}) => {
  const [t] = useTranslation('home-page');
  const [tCategories] = useTranslation('boat-categories');

  const buildUrl = () => {
    if (imgSrc) {
      const url = new URL(imgSrc); // to standardize url structure
      const imageParams = `w=${imgOptimizationWidth}`;
      return `${url.origin + url.pathname}?${imageParams}`;
    }
    return undefined;
  };

  const reviewRounded = reviewAverage && Math.round(parseFloat(reviewAverage));

  const location: string = breadcrumbs && breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1]?.name : '';
  const primaryCategoryShort = tCategories(`category-${primaryCategory}-short`);

  const categoryInLocation: string = primaryCategoryShort && location
    && t('listing-card--category-in-location', { category: primaryCategoryShort, location });

  return (
    <UniversalLink
      href={href}
      className={styles.root}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={buildUrl()}
        alt={headline}
        className={styles.image}
        loading="lazy"
      />
      <div className={styles.text}>
        <div className={styles.line1}>
          <Typography
            size="body-l"
            type="p"
            weight="semibold"
            className={styles.title}
          >
            {categoryInLocation}
          </Typography>
          <div className={styles.rating}>
            <Icon
              id="star-filled"
              size="m"
            />
            <Typography
              size="body-l"
              weight="semibold"
              type="p"
              className={styles.rating}
            >
              {reviewRounded}
            </Typography>
          </div>
        </div>
        <Typography
          size="body-l"
          type="p"
          className={styles.description}
        >
          {headline}
        </Typography>
      </div>
    </UniversalLink>
  );
};

export default ListingCardMinimal;
